import React from "react";
import styled from "styled-components";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import { media } from "../theme/breakpoints";

// ─── Grid ─────────────────────────────────────────────────────────────────────

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 20px;

  ${media.tablet} {
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    margin-bottom: 24px;
  }
`;

// ─── Card ─────────────────────────────────────────────────────────────────────

const StatBox = styled.div`
  position: relative;
  background: ${COLORS.PRIMARY_WHITE};
  border: 1px solid ${COLORS.BORDER_GREY};
  border-radius: 12px;
  padding: 14px 16px 14px 20px;
  overflow: hidden;
  transition:
    box-shadow 0.18s ease,
    transform 0.18s ease;

  /* Coloured left accent bar driven by $accent prop */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: 100%;
    border-radius: 2px 0 0 2px;
    background: ${({ $accent }) => $accent || COLORS.COLOR_DARK};
  }

  &:hover {
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.07);
    transform: translateY(-2px);
  }

  ${media.tablet} {
    padding: 18px 20px 18px 24px;
  }
`;

const IconWrap = styled.div`
  position: absolute;
  top: 12px;
  right: 14px;
  font-size: 20px;
  opacity: 0.18;
  color: ${({ $accent }) => $accent || COLORS.COLOR_DARK};
  pointer-events: none;

  ${media.tablet} {
    font-size: 24px;
  }
`;

const Label = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 10px;
  font-weight: 600;
  color: ${COLORS.INPUT_LABEL};
  text-transform: uppercase;
  letter-spacing: 0.07em;
  margin-bottom: 8px;

  ${media.tablet} {
    font-size: 10.5px;
  }
`;

const Value = styled.div`
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 20px;
  font-weight: 800;
  line-height: 1;
  color: ${({ $color }) => $color};

  ${media.tablet} {
    font-size: 26px;
  }
`;

const SubValue = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 10.5px;
  color: ${COLORS.INPUT_LABEL};
  margin-top: 5px;
  line-height: 1.4;

  ${media.tablet} {
    font-size: 11px;
  }
`;

// ─── Icon map (Tabler outline class names keyed by stat index) ─────────────────
// Consumers can pass an `icon` field in each stat object; we fall back gracefully.
const ICON_MAP = {
  0: "ti-calendar",
  1: "ti-clock",
  2: "ti-chart-bar",
  3: "ti-x",
};

// ─── Component ────────────────────────────────────────────────────────────────

const StatsRow = ({ stats }) => (
  <Grid>
    {stats.map((s, i) => {
      const icon = s.icon || ICON_MAP[i] || "ti-info-circle";
      return (
        <StatBox key={i} $accent={s.color || COLORS.COLOR_DARK}>
          <IconWrap $accent={s.color}>
            <i className={`ti ${icon}`} aria-hidden="true" />
          </IconWrap>
          <Label>{s.label}</Label>
          <Value $color={s.color || COLORS.COLOR_DARK}>{s.value}</Value>
          {s.subValue && <SubValue>{s.subValue}</SubValue>}
        </StatBox>
      );
    })}
  </Grid>
);

export default StatsRow;
